import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ExitTestDialogComponent } from '../shared/components/exit-test-dialog/exit-test-dialog.component';

/**
 * A test screen (SHARP / SENSE) that holds unsaved in-progress work.
 * Implement `isTestInProgress()` to report whether leaving now would discard
 * data; the guard only prompts when it returns `true`.
 */
export interface TestInProgressComponent {
  isTestInProgress(): boolean;
}

/**
 * Blocks navigation away from an in-progress test (including the native/browser
 * back button, which routes through `Location.back()` → popstate → guards) until
 * the user confirms they want to discard their progress. Nothing in these tests
 * is persisted until completion, so an unguarded back press silently wipes the
 * whole run.
 */
export const testInProgressGuard: CanDeactivateFn<TestInProgressComponent> =
  async (component) => {
    const dialog = inject(MatDialog);

    if (!component?.isTestInProgress?.()) return true;

    const ref = dialog.open(ExitTestDialogComponent, {
      disableClose: true,
      autoFocus: false,
      panelClass: 'exit-test-dialog-panel',
    });

    return !!(await firstValueFrom(ref.afterClosed()));
  };
